import { Injectable } from '@angular/core'
import { Firestore, collection, addDoc, serverTimestamp } from '@angular/fire/firestore'
import { CampaignTrackingService } from './campaign-tracking.service'
import { AuthenticationService } from './authentication.service'
import { GlobalService } from './global.service'
import { Router, NavigationEnd, Event } from '@angular/router'
import { filter } from 'rxjs/operators'

const PAGE_VIEWS_COLLECTION = 'pageViews'

@Injectable({
    providedIn: 'root',
})
export class PageViewService {
    private readonly VISITOR_COOKIE_NAME = 'parkupp_visitor_id'
    private readonly VISITOR_COOKIE_EXPIRY_DAYS = 365 // 1 year for visitor tracking
    private readonly SESSION_STORAGE_KEY = 'parkupp_session_id'
    
    private visitorId: string = ''
    private sessionId: string = ''
    private isTrackingEnabled = true
    
    // Deduplication: prevent double-tracking on page refresh
    private lastTrackedPath: string = ''
    private lastTrackedTime: number = 0
    private readonly DEDUP_WINDOW_MS = 2000 // 2 seconds
    
    // Paths to exclude from tracking (admin, merchant dashboard, etc.)
    private readonly EXCLUDED_PATH_PREFIXES = ['/admin', '/merchant']

    constructor(
        private firestore: Firestore,
        private campaignTrackingService: CampaignTrackingService,
        private authService: AuthenticationService,
        private globalService: GlobalService,
        private router: Router
    ) {
        this.initVisitorTracking()
        this.initSessionTracking()
        this.setupRouteTracking()
    }

    private waitForAuth(timeoutMs = 5000): Promise<void> {
        if (this.authService.auth.currentUser) return Promise.resolve()
        return new Promise((resolve) => {
            let settled = false
            let unsubscribe: () => void = () => {}
            const finish = () => {
                if (settled) return
                settled = true
                clearTimeout(timer)
                unsubscribe()
                resolve()
            }
            const timer = setTimeout(finish, timeoutMs)
            unsubscribe = this.authService.auth.onAuthStateChanged((user) => {
                if (user) finish()
            })
            if (settled) unsubscribe()
        })
    }

    /**
     * Initialize visitor ID from cookie or create a new one
     * This persists for 1 year to identify unique visitors
     */
    private initVisitorTracking(): void {
        const existingVisitorId = this.globalService.getCookie(this.VISITOR_COOKIE_NAME)
        
        if (existingVisitorId) {
            this.visitorId = existingVisitorId
        } else {
            // Generate a new UUID for this visitor
            this.visitorId = this.generateUUID()
            this.globalService.setCookie({
                name: this.VISITOR_COOKIE_NAME,
                value: this.visitorId,
                expireDays: this.VISITOR_COOKIE_EXPIRY_DAYS,
            })
        }
    }

    /**
     * Initialize session ID for this browser session
     * Resets when browser is closed
     */
    private initSessionTracking(): void {
        const existingSessionId = sessionStorage.getItem(this.SESSION_STORAGE_KEY)
        
        if (existingSessionId) {
            this.sessionId = existingSessionId
        } else {
            this.sessionId = this.generateUUID()
            sessionStorage.setItem(this.SESSION_STORAGE_KEY, this.sessionId)
        }
    }

    /**
     * Set up automatic page view tracking on route changes
     */
    private setupRouteTracking(): void {
        this.router.events
            .pipe(filter((event: Event): event is NavigationEnd => event instanceof NavigationEnd))
            .subscribe((event: NavigationEnd) => {
                // Small delay to ensure page title is updated
                setTimeout(() => {
                    this.trackPageView(event.urlAfterRedirects)
                }, 100)
            })
    }

    /**
     * Track a page view
     * Called automatically on route changes, can also be called manually
     */
    async trackPageView(pagePath?: string): Promise<void> {
        if (!this.isTrackingEnabled) return

        const currentPath = pagePath || window.location.pathname
        const now = Date.now()

        // Skip admin and other internal pages
        if (this.isExcludedPath(currentPath)) {
            return
        }

        // Deduplicate: skip if same path was tracked within the dedup window
        if (currentPath === this.lastTrackedPath && (now - this.lastTrackedTime) < this.DEDUP_WINDOW_MS) {
            return
        }

        // Update last tracked info
        this.lastTrackedPath = currentPath
        this.lastTrackedTime = now

        try {
            // pageViews writes need auth; the landing view usually fires before anonymous sign-in finishes
            await this.waitForAuth()
            const campaignData = this.campaignTrackingService.getCampaignData()
            const currentUser = this.authService.auth.currentUser
            
            const pageView = {
                // Visitor identification
                visitorId: this.visitorId,
                sessionId: this.sessionId,
                userId: currentUser?.uid || null,
                isAnonymous: currentUser?.isAnonymous ?? true,
                
                // Page information
                pageUrl: window.location.href,
                pagePath: pagePath || window.location.pathname,
                pageTitle: document.title,
                referrer: document.referrer || null,
                
                // Campaign tracking
                p_source: campaignData.p_source || null,
                p_campaign: campaignData.p_campaign || null,
                
                // Google Ads click IDs (durable server-side record for Safari ITP fallback)
                googleClickCapturedAt: campaignData.googleClickCapturedAt || null,
                gclid: campaignData.gclid || null,
                gbraid: campaignData.gbraid || null,
                wbraid: campaignData.wbraid || null,
                
                // Enterprise tracking
                denormEnterpriseKey: this.globalService.enterpriseKey || 'parkupp',
                
                // Device & Browser info
                userAgent: navigator.userAgent,
                screenWidth: window.screen.width,
                screenHeight: window.screen.height,
                language: navigator.language,
                
                // Timestamp
                createdAt: serverTimestamp(),
            }

            await addDoc(collection(this.firestore, PAGE_VIEWS_COLLECTION), pageView)
        } catch (error) {
            // Silent fail - don't spam console with tracking errors
        }
    }

    /**
     * Get the current visitor ID
     */
    getVisitorId(): string {
        return this.visitorId
    }

    /**
     * Get the current session ID
     */
    getSessionId(): string {
        return this.sessionId
    }

    /**
     * Check if a path should be excluded from tracking
     */
    private isExcludedPath(path: string): boolean {
        return this.EXCLUDED_PATH_PREFIXES.some(prefix => path.startsWith(prefix))
    }

    /**
     * Generate a UUID v4
     */
    private generateUUID(): string {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
            const r = (Math.random() * 16) | 0
            const v = c === 'x' ? r : (r & 0x3) | 0x8
            return v.toString(16)
        })
    }

    /**
     * Disable page view tracking (for testing or privacy)
     */
    disableTracking(): void {
        this.isTrackingEnabled = false
    }

    /**
     * Enable page view tracking
     */
    enableTracking(): void {
        this.isTrackingEnabled = true
    }
}

